// ─────────────────────────────────────────────────────────────────────────────
// NodeDetailModal — popup shown when a node row is clicked. Pure presentation:
// it takes the raw enriched `node` row, derives a view-model via buildNodeDetail,
// and renders a header + tab bar + the active tab + a per-tab footer. Each tab is
// its own module (./tabs/*); adding one means adding a file and a TABS entry.
// Shares the InviteModal / UserDetailModal visual language.
//
// Only the ACTIVE tab is mounted. That is what scopes the Logs tab's SSE
// connection to the time it is on screen — switching tabs or closing the modal
// unmounts it and closes the stream.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./NodeDetailModal.module.css";
import { buildNodeDetail } from "./buildNodeDetail";
import OverviewTab from "./tabs/OverviewTab";
import LogsTab from "./tabs/LogsTab";
import AlertsTab from "./tabs/AlertsTab";

const TABS = [
  { key: "overview", label: "Overview", Component: OverviewTab },
  { key: "logs", label: "Logs", Component: LogsTab },
  { key: "alerts", label: "Alerts", Component: AlertsTab },
];

export default function NodeDetailModal({ node, onClose }) {
  const [active, setActive] = useState("overview");

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!node) return null;
  const detail = buildNodeDetail(node);
  const ActiveTab = TABS.find((t) => t.key === active).Component;

  // Footer differs per tab: Overview offers node actions; the others just
  // navigate back to Overview / close.
  const footer = active === "overview" ? (
    <>
      {/* No reboot endpoint exists — disabled rather than silently doing
          nothing, which would read as "reboot sent". */}
      <button
        className={`${styles.footBtn} ${styles.footDanger}`}
        disabled
        title="Node reboot isn't available yet"
      >
        Reboot Node
      </button>
      <button
        className={`${styles.footBtn} ${styles.footPrimary}`}
        onClick={() => setActive("logs")}
      >
        View Logs
      </button>
    </>
  ) : (
    <>
      <button
        className={`${styles.footBtn} ${styles.footNeutral}`}
        onClick={() => setActive("overview")}
      >
        Back
      </button>
      <button className={`${styles.footBtn} ${styles.footPrimary}`} onClick={onClose}>
        Close
      </button>
    </>
  );

  // Portal to <body> so no ancestor's transform/backdrop-filter can trap the
  // fixed overlay (which would break centering and let the topbar overlap it).
  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <div className={styles.headerMeta}>
            <span className={styles.headerName}>{detail.id}</span>
            <span className={styles.headerRegion}>{detail.region}</span>
            <span
              className={styles.statusPill}
              style={{
                color: detail.statusColor,
                background: detail.statusBg,
                borderColor: detail.statusBorder,
              }}
            >
              <span className={styles.statusDot} style={{ background: detail.statusColor }} />
              {detail.statusLabel}
            </span>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">✕</button>
        </header>

        <nav className={styles.tabBar}>
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`${styles.tabBtn} ${active === t.key ? styles.tabActive : ""}`}
              onClick={() => setActive(t.key)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className={styles.body}>
          <ActiveTab detail={detail} nodeId={node.node_id} />
        </div>

        <footer className={styles.footer}>{footer}</footer>
      </div>
    </div>,
    document.body
  );
}
